/**
 * Start loading the next slot's video this many ms before the frame transition so decode has time to complete and the first painted frame is smooth.
 */
export const VIDEO_PRELOAD_LEAD_MS = 3000

/**
 * Start off-DOM decode of the next still this many ms before the frame transition.
 * Slightly longer than video lead: low-RAM Android often needs more time for large JPEG/PNG decode,
 * and we must not mount a full-size hidden &lt;img&gt; (compositor bleed).
 */
export const IMAGE_PRELOAD_LEAD_MS = 4000

/**
 * Minimum wall-clock dwell on a frame before the main-thread scheduler may advance again on a
 * sequential step (including collapsed slots / rAF storms; see #1225).
 */
export const MIN_FRAME_WALL_DWELL_MS = 400

/** Fraction of the on-air slot duration used as the minimum wall dwell (healthy slots; collapsed slots use {@link MIN_FRAME_WALL_DWELL_MS}). */
export const MIN_FRAME_WALL_DWELL_FRACTION = 0.85

/**
 * Max time to keep the outgoing creative visible while the incoming one becomes paint-ready
 * (image decode / widget). Then fall back to today’s hard cut.
 * Video targets use {@link HOLD_UNTIL_READY_VIDEO_TIMEOUT_MS} — `playing` often lands after ~200ms.
 * Image/widget budget matches ~one crossfade so cold stills can still clear with `ready` + fade.
 */
export const HOLD_UNTIL_READY_TIMEOUT_MS = 400

/**
 * Hold budget when the incoming main facet is video (`video-blob` / `hls`).
 * Longer than the image/widget default so sticky can clear with `ready` + crossfade instead of a timeout hard-cut.
 */
export const HOLD_UNTIL_READY_VIDEO_TIMEOUT_MS = 800

import { DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS } from '@lucimark/shared/models/tenant'

/**
 * Phase C opacity dissolve when sticky hold clears with paint-ready incoming, or warm ready-at-commit.
 * Used when presentation crossfade is enabled (tenant default on; Studio / storage / query opt-out).
 * Tenant may override via `playerCreativeCrossfadeMs`; this constant is the fallback / default.
 */
export const CROSSFADE_MS = DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS
